import type { CSSProperties, ReactNode } from 'react'
import { cn } from '@/lib/utils'

/**
 * The shared visual vocabulary of the site: the crest, the engraved textures,
 * the section headings and the ticker. Everything here is CSS-driven so it
 * scales cleanly and costs no extra network requests beyond the four brand
 * masks in /public/brand.
 */

export function Crest({ className, alt = '' }: { className?: string; alt?: string }) {
  return (
    <img
      src="/brand/logo.webp"
      alt={alt}
      width={64}
      height={64}
      className={cn('object-contain select-none', className)}
      draggable={false}
    />
  )
}

/**
 * Paints one of the brand masks in the current text colour. Because the assets
 * ship as single-channel masks, the same file works in blood red on black or
 * bone on red without a second download.
 */
export function BrandMask({
  src,
  className,
  tile,
  style,
  'aria-hidden': ariaHidden = true,
}: {
  src: 'illustration' | 'lines' | 'mesh' | 'map'
  className?: string
  tile?: boolean
  style?: CSSProperties
  'aria-hidden'?: boolean
}) {
  return (
    <div
      aria-hidden={ariaHidden}
      className={cn('brand-mask', tile && 'brand-mask-tile', className)}
      style={{ '--brand-mask': `url(/brand/${src}.webp)`, ...style } as CSSProperties}
    />
  )
}

/** Faint tiled starburst texture used to lift large flat sections. */
export function MeshTexture({
  className,
  opacity = 0.06,
}: {
  className?: string
  opacity?: number
}) {
  return (
    <BrandMask
      src="mesh"
      tile
      className={cn('text-blood pointer-events-none absolute inset-0', className)}
      style={{ opacity, maskSize: '340px' }}
    />
  )
}

/** The vertical column rules from the brand sheet, drawn rather than imaged. */
export function RuleColumns({ className, gap = '12.5%' }: { className?: string; gap?: string }) {
  return (
    <div
      aria-hidden
      className={cn('rule-grid pointer-events-none absolute inset-0 opacity-40', className)}
      style={{ '--rule-gap': gap } as CSSProperties}
    />
  )
}

interface SectionHeadingProps {
  eyebrow?: string
  title: string
  description?: string
  align?: 'left' | 'center'
  action?: ReactNode
  className?: string
  id?: string
}

export function SectionHeading({
  eyebrow,
  title,
  description,
  align = 'left',
  action,
  className,
  id,
}: SectionHeadingProps) {
  return (
    <div
      className={cn(
        'flex flex-col gap-6 md:flex-row md:items-end md:justify-between',
        align === 'center' && 'md:flex-col md:items-center md:text-center',
        className
      )}
    >
      <div className={cn('max-w-2xl', align === 'center' && 'mx-auto')}>
        {eyebrow ? (
          <div className="mb-4 flex items-center gap-3">
            <span className="bg-blood h-px w-8" aria-hidden />
            <span className="eyebrow text-blood">{eyebrow}</span>
          </div>
        ) : null}
        <h2
          id={id}
          className="condensed text-balance-safe text-bone text-[clamp(1.9rem,4.6vw,3.4rem)] leading-[0.95] font-bold"
        >
          {title}
        </h2>
        {description ? (
          <p className="text-bone-dim mt-4 max-w-xl text-[1.0625rem] leading-relaxed">
            {description}
          </p>
        ) : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  )
}

/**
 * Infinite ticker. The track holds two identical copies and translates by
 * exactly -50%, so the loop is seamless at any content length.
 */
export function Marquee({
  items,
  className,
  speed = 40,
}: {
  items: string[]
  className?: string
  speed?: number
}) {
  if (items.length === 0) return null
  const track = [...items, ...items]

  return (
    <div
      className={cn('group border-hairline relative flex overflow-hidden border-y py-3', className)}
      aria-hidden
    >
      <div
        className="flex shrink-0 animate-[marquee_var(--speed)_linear_infinite] items-center gap-10 pr-10 group-hover:[animation-play-state:paused]"
        style={{ '--speed': `${speed}s` } as CSSProperties}
      >
        {track.map((item, index) => (
          // The track is the item list duplicated, so the index is what
          // distinguishes the two copies of the same string.
          // eslint-disable-next-line react/no-array-index-key
          <span key={`${index}-${item}`} className="flex shrink-0 items-center gap-10">
            <span className="condensed text-bone-dim text-sm tracking-[0.28em]">{item}</span>
            <span className="bg-blood size-1.5 shrink-0 rotate-45" />
          </span>
        ))}
      </div>
    </div>
  )
}

/** Number-led stat block, as on the hero. */
export function StatBlock({
  value,
  label,
  detail,
  className,
}: {
  value: string
  label: string
  detail?: string
  className?: string
}) {
  return (
    <div className={cn('group', className)}>
      <div className="display text-blood group-hover:text-blood-bright text-[clamp(2.75rem,6vw,4.5rem)] leading-none font-bold transition-colors">
        {value}
      </div>
      <div className="condensed text-bone mt-1 text-[0.9375rem] font-semibold tracking-[0.14em]">
        {label}
      </div>
      {detail ? <p className="text-bone-faint mt-2 text-sm leading-snug">{detail}</p> : null}
    </div>
  )
}

/** The red vertical spine with rotated type, lifted from the wireframe. */
export function SideRail({ label }: { label: string }) {
  return (
    <div className="bg-blood absolute top-0 bottom-0 left-0 hidden w-14 shrink-0 items-center justify-center lg:flex">
      <span
        className="condensed text-bone text-[0.6875rem] font-semibold tracking-[0.4em] whitespace-nowrap"
        style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
      >
        {label}
      </span>
    </div>
  )
}

/** Bone-on-ink container used by every page section. */
export function Container({
  className,
  children,
  size = 'default',
}: {
  className?: string
  children: ReactNode
  size?: 'default' | 'wide' | 'narrow'
}) {
  return (
    <div
      className={cn(
        'mx-auto w-full px-5 sm:px-8',
        size === 'default' && 'max-w-[86rem]',
        size === 'wide' && 'max-w-[100rem]',
        size === 'narrow' && 'max-w-3xl',
        className
      )}
    >
      {children}
    </div>
  )
}
